import { useState } from 'react'
import { LoadingButton } from '@/components/LoadingButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateOrganization, getGetOrganizationQueryKey } from '@/api/auth/organizations/organizations'
import { getGetMeOrganizationsQueryKey } from '@/api/auth/self/self'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { CountryCombobox } from './CountryCombobox'

interface SetBillingCountryDialogProps {
  orgId: string
  orgName: string
  onComplete: () => void
}

/**
 * Blocking dialog component for administrators to set billing country on organizations that don't have one
 */
export function SetBillingCountryDialog({
  orgId,
  orgName,
  onComplete,
}: SetBillingCountryDialogProps) {
  const queryClient = useQueryClient();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const updateOrganization = useUpdateOrganization();

  /**
   * Handle form submission - update org with billing country
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCountry || isSaving) return;

    setIsSaving(true);
    try {
      await updateOrganization.mutateAsync({
        orgId,
        data: {
          name: orgName,
          billingCountry: selectedCountry,
        }
      });

      toast.success(t.dialogs.setBillingCountry.billingCountryUpdated);

      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: getGetMeOrganizationsQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getGetOrganizationQueryKey(orgId) });

      onComplete();
    } catch (error: unknown) {
      handleApiError(error, { context: 'SetBillingCountryDialog.updateOrganization' });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={true}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[425px]"
        onPointerDownOutside={(e) => { e.preventDefault(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t.dialogs.setBillingCountry.title}</DialogTitle>
          <DialogDescription>{t.dialogs.setBillingCountry.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { void handleSubmit(e); }}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="billing-country">{t.dialogs.setBillingCountry.billingCountry}</Label>
              <CountryCombobox
                id="billing-country"
                value={selectedCountry}
                onChange={setSelectedCountry}
                disabled={isSaving}
              />
            </div>
          </div>
          <DialogFooter>
            <LoadingButton type="submit" disabled={!selectedCountry} isLoading={isSaving}>
              {t.dialogs.setBillingCountry.save}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
